import type { TypedDocumentNode } from "@apollo/client";
import gql from "graphql-tag";

type Pagination<T> = {
  current: number
  total: number
  perPage: number
  from: number
  to: number
  previous: number | null
  next: number | null
  data: T[]
};

type BrandSummary = {
  id: string
  brand_name: string
  short_name: string
  created_at: string
  is_active: boolean
};

export type BrandsQueryVariables = {
  page?: number
  limit?: number
  isActive?: boolean
};

export type BrandsQuery = {
  brands: Pagination<BrandSummary>
};

export const BRANDS_QUERY: TypedDocumentNode<BrandsQuery, BrandsQueryVariables> = gql`
  query brands($page: Int, $limit: Int, $isActive: Boolean) {
    brands(page: $page, limit: $limit, isActive: $isActive) {
      data {
        id
        brand_name
        short_name
        created_at
        is_active
      }
      total
      perPage
      current
      from
      to
      previous
      next
    }
  }
`;

export type BrandsFilterQuery = {
  brands: {
    data: Array<{
      id: string
      brand_name: string
      short_name: string
    }>
  }
};

export const BRANDS_FILTER_QUERY: TypedDocumentNode<BrandsFilterQuery, BrandsQueryVariables> = gql`
  query brands($page: Int, $limit: Int, $isActive: Boolean) {
    brands(page: $page, limit: $limit, isActive: $isActive) {
      data {
        id
        brand_name
        short_name
      }
    }
  }
`;

export type GetBrandActiveTemplatesQueryVariables = {
  brand: number
  search: string
  channel: string
};

export type GetBrandActiveTemplatesQuery = {
  emailTemplates: {
    data: Array<{
      id: string
      name: string
      type: string
    }>
  }
};

export const GET_BRAND_ACTIVE_TEMPLATES: TypedDocumentNode<GetBrandActiveTemplatesQuery, GetBrandActiveTemplatesQueryVariables> = gql`
  query brandTemplates($brand: Int!, $search: String!, $channel: BigInt!) {
    emailTemplates(
      search: $search
      brand: $brand
      isActive: true
      channel: $channel
    ) {
      data {
        id
        name
        type
      }
    }
  }
`;

export type GetBrandActiveSmtpQueryVariables = {
  brand: number
};

export type GetBrandActiveSmtpQuery = {
  brandSmtp: {
    data: Array<{
      id: string
      title: string
      connection: {
        id: string
      } | null
    }>
  }
};

export const GET_BRAND_ACTIVE_SMTP: TypedDocumentNode<GetBrandActiveSmtpQuery, GetBrandActiveSmtpQueryVariables> = gql`
  query brandSMTP($brand: Int!) {
    brandSmtp(brand: $brand, isActive: true) {
      data {
        id
        title
        connection {
          id
        }
      }
    }
  }
`;

export type GetBrandByIdQueryVariables = {
  id: string
};

export type GetBrandByIdQuery = {
  getBrandById: {
    id: string
    short_name: string
    mpt_id: string | null
    brand_name: string
    is_active: boolean
    tier: string | null
    brand_groups: {
      parent_brand_name: string
      short_name: string
    } | null
  }
};

export const GET_BRAND_BY_ID: TypedDocumentNode<GetBrandByIdQuery, GetBrandByIdQueryVariables> = gql`
  query getBrandById($id: ID!) {
    getBrandById(id: $id) {
      id
      short_name
      mpt_id
      brand_name
      is_active
      tier
      brand_groups {
        parent_brand_name
        short_name
      }
    }
  }
`;

export type UpdateBrandMutationVariables = {
  id: string
  data: {
    brand_name?: string
    short_name?: string
    is_active?: boolean
    tier?: string
  }
};

export type UpdateBrandMutation = {
  updateBrand: {
    created_at: string
  }
};

export const UPDATE_BRAND: TypedDocumentNode<UpdateBrandMutation, UpdateBrandMutationVariables> = gql`
  mutation updateBrand($id: ID!, $data: BrandUpdateInput!) {
    updateBrand(data: $data, id: $id) {
      created_at
    }
  }
`;

export type GetBrandActiveConnectionsQueryVariables = {
  brand: number
};

export type GetBrandActiveConnectionsQuery = {
  getConnections: Array<{
    id: string
    title: string
    oauth2: {
      id: string
      title: string
      mail_client: string
    } | null
    smtp: {
      id: string
      title: string
    } | null
  }>
};

export const GET_BRAND_ACTIVE_CONNECTIONS: TypedDocumentNode<GetBrandActiveConnectionsQuery, GetBrandActiveConnectionsQueryVariables> = gql`
  query getConnections($brand: Int!) {
    getConnections(brand: $brand, isActive: true) {
      id
      title
      oauth2 {
        id
        title
        mail_client
      }
      smtp {
        id
        title
      }
    }
  }
`;

export type GetBrandChannelsQueryVariables = {
  id: string
  limit?: number
  page?: number
};

export type GetBrandChannelsQuery = {
  getBrandChannels: Pagination<{
    id: string
    name: string
    tld: string | null
    status: string
  }>
};

export const GET_BRAND_CHANNELS: TypedDocumentNode<GetBrandChannelsQuery, GetBrandChannelsQueryVariables> = gql`
  query getBrandChannels($id: ID!, $limit: Int, $page: Int) {
    getBrandChannels(id: $id, limit: $limit, page: $page) {
      data {
        id
        name
        tld
        status
      }
      current
      from
      next
      perPage
      previous
      to
      total
    }
  }
`;

export type GetNotBrandChannelsQueryVariables = {
  brandId: string
};

export type GetNotBrandChannelsQuery = {
  getNotBrandChannels: Array<{
    id: string
    name: string
  }>
};

export const GET_NOT_BRAND_CHANNELS: TypedDocumentNode<GetNotBrandChannelsQuery, GetNotBrandChannelsQueryVariables> = gql`
  query getNotBrandChannels($brandId: ID!) {
    getNotBrandChannels(brand_id: $brandId) {
      id
      name
    }
  }
`;

export type AttachBrandChannelMutationVariables = {
  data: {
    brand_id: number
    channel_id: number
  }
};

export type AttachBrandChannelMutation = {
  attachBrandChannel: {
    id: string
    brand_id: number
    channel_id: number
  }
};

export const ATTACH_BRAND_CHANNEL: TypedDocumentNode<AttachBrandChannelMutation, AttachBrandChannelMutationVariables> = gql`
  mutation Mutation($data: BrandChannelInput!) {
    attachBrandChannel(data: $data) {
      id
      brand_id
      channel_id
    }
  }
`;

export type DetachBrandChannelMutationVariables = AttachBrandChannelMutationVariables;

export type DetachBrandChannelMutation = {
  detachBrandChannel: boolean
};

export const DETACH_BRAND_CHANNEL: TypedDocumentNode<DetachBrandChannelMutation, DetachBrandChannelMutationVariables> = gql`
  mutation Mutation($data: BrandChannelInput!) {
    detachBrandChannel(data: $data)
  }
`;

export type GetBrandAliasesQueryVariables = {
  brand_id: string
};

export type GetBrandAliasesQuery = {
  getAliasList: Array<{
    id: string
    brand_id: number
    alias: string
    created_at: string
    updated_at: string | null
  }>
};

export const GET_BRAND_ALIASES: TypedDocumentNode<GetBrandAliasesQuery, GetBrandAliasesQueryVariables> = gql`
  query ($brand_id: ID!) {
    getAliasList(brand_id: $brand_id) {
      id
      brand_id
      alias
      created_at
      updated_at
    }
  }
`;

export type UpdateBrandAliasMutationVariables = {
  id: string
  data: {
    alias: string
  }
};

export type UpdateBrandAliasMutation = {
  updateAlias: {
    id: string
    brand_id: number
    alias: string
  }
};

export const UPDATE_BRAND_ALIAS: TypedDocumentNode<UpdateBrandAliasMutation, UpdateBrandAliasMutationVariables> = gql`
  mutation ($id: ID!, $data: AliasUpdateGqlModel!) {
    updateAlias(id: $id, data: $data) {
      id
      brand_id
      alias
    }
  }
`;

export type CreateBrandAliasMutationVariables = {
  data: {
    alias: string
    brand_id: string
  }
};

export type CreateBrandAliasMutation = {
  createAlias: {
    id: string
    brand_id: number
    alias: string
  }
};

export const CREATE_BRAND_ALIAS: TypedDocumentNode<CreateBrandAliasMutation, CreateBrandAliasMutationVariables> = gql`
  mutation ($data: AliasCreateGqlModel!) {
    createAlias(data: $data) {
      id
      brand_id
      alias
    }
  }
`;

export type RemoveBrandAliasMutationVariables = {
  id: string
};

export type RemoveBrandAliasMutation = {
  removeAlias: boolean
};

export const REMOVE_BRAND_ALIAS: TypedDocumentNode<RemoveBrandAliasMutation, RemoveBrandAliasMutationVariables> = gql`
  mutation ($id: ID!) {
    removeAlias(id: $id)
  }
`;

export type GetBrandProductsQueryVariables = {
  params: {
    limit?: number
    page?: number
    search?: string
    brand?: number
    status?: string
  }
};

export type GetBrandProductsQuery = {
  getProducts: Pagination<{
    id: string
    label: string
    sku: string
    asin: string | null
    fnsku: string | null
    updated_at: string
    status: string
  }>
};

export const GET_BRAND_PRODUCTS: TypedDocumentNode<GetBrandProductsQuery, GetBrandProductsQueryVariables> = gql`
  query getProductsQuery($params: ParamsInput!) {
    getProducts(params: $params) {
      current
      total
      perPage
      from
      to
      data {
        id
        label
        sku
        asin
        fnsku
        updated_at
        status
      }
      previous
      next
    }
  }
`;

export type GetBrandCompetitorsQueryVariables = {
  args: {
    page?: number
    limit?: number
    search?: string
    status?: string
    channelId?: number
  }
};

export type GetBrandCompetitorsQuery = {
  getCompetitorBrands: Pagination<{
    brand_name: string
    smart_scout_id: string
    status: string
    channel_id: string
    updated_at: string
    channels: Array<{
      name: string
    }>
  }>
};

export const GET_BRAND_COMPETITORS: TypedDocumentNode<GetBrandCompetitorsQuery, GetBrandCompetitorsQueryVariables> = gql`
  query getCompetitorBrandsQuery($args: GetCompetitorBrandsArgs!) {
    getCompetitorBrands(args: $args) {
      current
      total
      perPage
      from
      to
      data {
        brand_name
        smart_scout_id
        status
        channel_id
        updated_at
        channels {
          name
        }
      }
      previous
      next
    }
  }
`;

export type GetCompetitorBrandsWithoutPaginationQueryVariables = {
  channelId: number
  search?: string
};

export type GetCompetitorBrandsWithoutPaginationQuery = {
  getCompetitorBrandsWithoutPagination: Array<{
    id: string
    name: string
  }>
};

export const GET_COMPETITOR_BRANDS_WITHOUT_PAGINATION: TypedDocumentNode<GetCompetitorBrandsWithoutPaginationQuery, GetCompetitorBrandsWithoutPaginationQueryVariables> = gql`
  query getCompetitorBrandsWithoutPagination(
    $channelId: Float!
    $search: String
  ) {
    getCompetitorBrandsWithoutPagination(
      channelId: $channelId
      search: $search
    ) {
      id
      name
    }
  }
`;

export type UpdateBrandShippingDestinationMutationVariables = {
  id: string
  data: {
    shipping_destination: string
  }
};

export type UpdateBrandShippingDestinationMutation = {
  updateBrandShippingDestination: {
    id: string
  }
};

export const UPDATE_BRAND_SHIPPING_DESTINATION: TypedDocumentNode<UpdateBrandShippingDestinationMutation, UpdateBrandShippingDestinationMutationVariables> = gql`
  mutation updateBrandShippingDestination(
    $id: ID!
    $data: UpdateShippingDestinationInput!
  ) {
    updateBrandShippingDestination(id: $id, data: $data) {
      id
    }
  }
`;

export type ConnectBrandWithCompetitorMutationVariables = {
  args: {
    brandId: number
    competitorBrandId: number
  }
};

export type ConnectBrandWithCompetitorMutation = {
  connectBrandWithCompetitorBrand: boolean
};

export const CONNECT_BRAND_WITH_COMPETITOR: TypedDocumentNode<ConnectBrandWithCompetitorMutation, ConnectBrandWithCompetitorMutationVariables> = gql`
  mutation connectBrandWithCompetitorBrand(
    $args: ConnectBrandWithCompetitorBrandArgs!
  ) {
    connectBrandWithCompetitorBrand(args: $args)
  }
`;
